import { DailyEventObjectAppMessage } from '@daily-co/daily-js';
import { useAppMessage, useLocalParticipant } from '@daily-co/daily-react';
import { useCallback } from 'react';
import {
  atom,
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
} from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { skinToneMap } from '/hooks/useEmojiSkinTone';

// The base available audience reaction emoji
export const availableReactionEmoji = [
  '👏',
  '👍',
  '👎',
  '👋',
  '❤️',
  '😂',
  '🤯',
  '🔥',
  '🎉',
];

// The available audience reaction emoji with modifiers
const possibleEmojiCombinations = [];
const generateAllowedEmojiList = () => {
  const emojiWithModifiers = (emojis) => {
    emojis.map((emoji) => {
      for (let i = 0; i < Object.values(skinToneMap).length; i++) {
        possibleEmojiCombinations.push(emoji + Object.values(skinToneMap)[i]);
      }
    });
    return possibleEmojiCombinations;
  };

  emojiWithModifiers(['👏', '👍', '👎', '👋']);
};

generateAllowedEmojiList();

// All allowed audience reaction emoji
export const allowedReactionEmoji = [
  ...possibleEmojiCombinations,
  ...availableReactionEmoji,
];

export type AllowedEmojis = typeof availableReactionEmoji[number];

// We don't want to allow crafty participants to send any old emoji or message using
// sendAppMessage, so we check if the emoji is allowed.
function emojiIsAllowed(r: string): r is AllowedEmojis {
  return allowedReactionEmoji.includes(r);
}

export interface EmojiReaction {
  created?: Date;
  emoji: AllowedEmojis;
  id: string;
  xPosition?: number;
  positionOffset?: number;
  sessionId: string;
  room: string;
}

interface EmojiReactionAppMessageData {
  event: 'sync-emoji-reaction';
  reaction?: EmojiReaction;
}

type AppMessageData = EmojiReactionAppMessageData;

const currentEmojiReactionState = atom<EmojiReaction>({
  key: 'current-emoji-reaction',
  default: null,
});

const showEmojiReactionsMenuState = atom<boolean>({
  key: 'show-emoji-reactions',
  default: false,
});

export const useShowEmojiReactionsMenu = () => {
  const [showEmojiReactionsMenu, setShowEmojiReactionsMenu] = useRecoilState(
    showEmojiReactionsMenuState
  );
  return { setShowEmojiReactionsMenu, showEmojiReactionsMenu };
};

export const useEmojiReactions = () => {
  const localParticipant = useLocalParticipant();
  const { currentBreakoutRoom } = useBreakoutSessionState();

  const currentEmojiReaction = useRecoilValue(currentEmojiReactionState);
  const addCurrentEmojiReaction = useRecoilCallback(
    ({ set }) =>
      (r: EmojiReaction, xPosition?: number) => {
        set(currentEmojiReactionState, (old) => {
          // Avoid unintentional double reactions
          if (old && old.id === r.id) {
            return old;
          }
          const newR = { ...r };
          // create new date when reaction arrives at client,
          // so differentiating past reactions when changing call views works correctly
          newR.created = new Date();
          newR.xPosition = xPosition;
          // Random position offset between -25 and 25 to make it look more 'bubbly'
          newR.positionOffset = Math.floor(
            Math.random() * 25 * (Math.round(Math.random()) * 2 - 1)
          );
          return newR;
        });
      },
    []
  );

  const sendAppMessage = useAppMessage<AppMessageData>({
    onAppMessage: useCallback(
      (ev: DailyEventObjectAppMessage) => {
        switch (ev?.data?.event) {
          case 'sync-emoji-reaction':
            if (emojiIsAllowed(ev?.data?.reaction?.emoji)) {
              addCurrentEmojiReaction(ev?.data?.reaction);
            }
            break;
        }
      },
      [addCurrentEmojiReaction]
    ),
  });

  const sendReaction = useCallback(
    (r: string, position: number) => {
      if (!localParticipant?.session_id) return;

      const sessionId = localParticipant.session_id;
      const reaction = {
        emoji: r,
        id: uuidv4(),
        sessionId,
        room: currentBreakoutRoom.id,
      };
      sendAppMessage({
        event: 'sync-emoji-reaction',
        reaction,
      });
      // Add own emoji with custom position for the fly-up animation
      addCurrentEmojiReaction(reaction, position);
    },
    [
      addCurrentEmojiReaction,
      currentBreakoutRoom.id,
      localParticipant,
      sendAppMessage,
    ]
  );

  return {
    currentEmojiReaction,
    sendReaction,
  };
};
